import { Check, ChevronDown, Lock, TrendingUp } from "lucide-react"

import { cn } from "@/lib/utils"
import { IconTile, Panel, SectionHead } from "./Panel"
import { formatCents, savingsPct } from "./format"

// Standard-vs-Insider pricing: a 30,000-word book by tier, then the full
// per-service breakdown behind a disclosure. All money is integer cents from
// the presenter (real PriceList rows). `subscriber` swaps the framing (member
// "Your Insider rates" vs offer "Standard vs Insider") and, on the offer side
// when every row is identical today, shows the "Same prices today" note.
//
// Below 720 both tables pan inside their own wrapper, keeping a floor width
// for the four money columns while the first column stays pinned on an opaque
// surface so the row label remains readable.

const TH = "px-[22px] py-[12px] text-right text-[10.5px] font-bold tracking-[0.08em] text-ink-3 uppercase max-[720px]:px-[14px]"
const TD = "border-t border-border px-[22px] py-[13px] text-right whitespace-nowrap max-[720px]:px-[14px]"
const PIN = "text-left max-[720px]:sticky max-[720px]:left-0 max-[720px]:z-[1] max-[720px]:bg-card"
const INS = "bg-surface3 group-hover/row:bg-secondary"
const ROW = "group/row [&>td]:transition-colors hover:[&>td:not(.ins)]:bg-surface3"

function SavingsCell({ standard, insider, showAmount }) {
  const pct = savingsPct(standard, insider)
  if (!pct) return <span className="text-[12.5px] text-ink-3">Same today</span>

  return (
    <div className="flex items-center justify-end gap-[8px]">
      {showAmount && <span className="text-[13.5px] font-semibold text-done">{formatCents(standard - insider)}</span>}
      <span className="inline-flex items-center gap-[4px] rounded-full bg-done/15 px-[8px] py-[2px] text-[11.5px] font-bold text-done">
        {!showAmount && <TrendingUp className="size-3" strokeWidth={2.4} />}
        {pct}%
      </span>
    </div>
  )
}

function Head({ first }) {
  return (
    <thead>
      <tr>
        <th className={cn(TH, PIN)}>{first}</th>
        <th className={TH}>Standard</th>
        <th className={cn(TH, "bg-surface3 text-foreground")}>Insider</th>
        <th className={TH}>You save</th>
      </tr>
    </thead>
  )
}

function PriceRow({ label, unit, standard, insider, strike, showAmount }) {
  return (
    <tr className={ROW}>
      <td className={cn(TD, PIN)}>
        <div className="text-[14px] font-semibold text-foreground">{label}</div>
        {unit && <div className="mt-[2px] text-[12px] text-ink-3">{unit}</div>}
      </td>
      <td className={TD}>
        <span className={cn("text-[14px]", strike && savingsPct(standard, insider) ? "text-ink-3 line-through" : "text-muted-foreground")}>
          {formatCents(standard)}
        </span>
      </td>
      <td className={cn(TD, INS, "ins")}>
        <span className="font-display text-[15px] font-bold text-foreground">{formatCents(insider)}</span>
      </td>
      <td className={TD}>
        <SavingsCell standard={standard} insider={insider} showAmount={showAmount} />
      </td>
    </tr>
  )
}

function BookTable({ rows }) {
  const words = rows[0]?.word_count ?? 30_000
  return (
    <Panel className="overflow-hidden">
      <div className="flex flex-wrap items-baseline justify-between gap-x-[12px] gap-y-[4px] border-b border-border px-[22px] py-[16px] max-[720px]:px-[16px]">
        <h3 className="font-display text-[14.5px] font-bold">A full {words.toLocaleString("en-US")}-word book, by tier</h3>
        <span className="text-[12.5px] text-ink-3">Writing cost only · same math as your checkout</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse max-[720px]:min-w-[480px]">
          <Head first="Tier" />
          <tbody>
            {rows.map((row) => (
              <PriceRow key={row.label} label={row.label} standard={row.standard_cents} insider={row.insider_cents} strike showAmount />
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function Breakdown({ groups }) {
  return (
    <details className="group/bd overflow-hidden rounded-2xl border border-border bg-card">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-[14px] px-[22px] py-[16px] hover:bg-surface3 max-[720px]:px-[16px] [&::-webkit-details-marker]:hidden">
        <div className="flex items-center gap-[13px]">
          <IconTile>
            <TrendingUp strokeWidth={1.9} />
          </IconTile>
          <div>
            <b className="block text-[14px] font-bold">See the full price breakdown</b>
            <span className="text-[12.5px] text-ink-3">Every service, every tier: writing, editing, covers, formatting, and more</span>
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-[5px] text-[12.5px] font-semibold text-muted-foreground">
          <span className="group-open/bd:hidden">Expand</span>
          <span className="hidden group-open/bd:inline">Collapse</span>
          <ChevronDown className="size-[15px] transition-transform group-open/bd:rotate-180 motion-reduce:transition-none" strokeWidth={2} />
        </span>
      </summary>
      <div className="border-t border-border">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse max-[720px]:min-w-[480px]">
            <Head first="Service" />
            <tbody>
              {groups.map((group) => (
                <GroupRows key={group.title} group={group} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-[12px] border-t border-border bg-surface3 px-[22px] py-[14px] text-[13px] leading-[1.5] text-muted-foreground max-[720px]:px-[16px]">
          <Check className="size-4 shrink-0" strokeWidth={2} />
          <p>
            <b className="text-foreground">Insider pricing applies to every TUW service automatically</b>, including any
            service not shown above.
          </p>
        </div>
      </div>
    </details>
  )
}

function GroupRows({ group }) {
  return (
    <>
      <tr>
        <td colSpan={4} className="border-t border-border bg-secondary px-[22px] py-[10px] max-[720px]:px-[14px]">
          <b className="text-[10.5px] font-bold tracking-[0.1em] text-muted-foreground uppercase">{group.title}</b>
        </td>
      </tr>
      {group.items.map((item) => (
        <PriceRow key={item.label} label={item.label} unit={item.unit} standard={item.standard_cents} insider={item.insider_cents} />
      ))}
    </>
  )
}

export default function PriceComparison({ comparison, subscriber }) {
  if (!comparison) return null

  return (
    <>
      <SectionHead id="compare" title={subscriber ? "Your Insider rates" : "Standard vs Insider pricing"}>
        {subscriber
          ? "These are your current Insider rates. When TUW next raises standard prices, your Insider rates move up to the previous standard tier, always one step behind the market."
          : "Subscribe today. From then on you'll pay the Insider rates below, and when standard goes up, you'll stay one step behind."}
      </SectionHead>

      {!subscriber && comparison.allSame && (
        <div className="flex items-start gap-[12px] rounded-xl border border-border bg-card px-[16px] py-[14px] text-[13.5px] leading-[1.55] text-muted-foreground">
          <IconTile>
            <Lock strokeWidth={1.9} />
          </IconTile>
          <p>
            <b className="text-foreground">Same prices today.</b> Your savings begin the next time TUW raises standard
            rates. You&apos;ll stay on the prices below while everyone else moves up to the new tier.
          </p>
        </div>
      )}

      <BookTable rows={comparison.bookRows ?? []} />
      <Breakdown groups={comparison.groups ?? []} />
    </>
  )
}
